/**
 * Tool invocation service.
 *
 * Read-only client for the observability API. Two surfaces:
 *   - per-conversation listing for the in-chat debug panel (retroactive
 *     fetch after page refresh — live events arrive via the SSE stream)
 *   - tenant-wide listing + detail for the super-admin Tool Activity page
 */
import api from '@/services/api';

export interface ToolInvocation {
  invocation_id: string;
  tenant_id: string;
  conversation_id: string | null;
  message_id: string | null;
  user_id: string | null;
  parent_invocation_id: string | null;
  tool_name: string;
  status: 'pending' | 'success' | 'error';
  started_at: string | null;
  completed_at: string | null;
  latency_ms: number | null;
  input_json?: unknown;
  output_json?: unknown;
  input_truncated: boolean;
  output_truncated: boolean;
  error_text: string | null;
  metadata: Record<string, unknown>;
}

export interface PaginatedToolInvocations {
  invocations: ToolInvocation[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export interface TenantToolActivityFilters {
  page?: number;
  per_page?: number;
  tool_name?: string;
  status?: 'pending' | 'success' | 'error';
  user_id?: string;
  conversation_id?: string;
  from?: string;  // ISO-8601
  to?: string;    // ISO-8601
}

class ToolInvocationService {
  // -------- in-conversation retroactive fetch ----------------
  async listForConversation(conversationId: string): Promise<ToolInvocation[]> {
    const { data } = await api.get<{ success: boolean; invocations: ToolInvocation[] }>(
      `/conversations/${conversationId}/tool-invocations`,
    );
    return data.invocations || [];
  }

  // -------- super-admin tenant-wide listing ------------------
  async listForTenant(
    tenantId: string,
    filters: TenantToolActivityFilters = {},
  ): Promise<PaginatedToolInvocations> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null || v === '') continue;
      params.set(k, String(v));
    }
    const { data } = await api.get<PaginatedToolInvocations & { success: boolean }>(
      `/super-admin/tenants/${tenantId}/tool-activity${params.toString() ? `?${params}` : ''}`,
    );
    return { invocations: data.invocations || [], pagination: data.pagination };
  }

  async getDetailForTenant(tenantId: string, invocationId: string): Promise<ToolInvocation> {
    const { data } = await api.get<{ success: boolean; invocation: ToolInvocation }>(
      `/super-admin/tenants/${tenantId}/tool-activity/${invocationId}`,
    );
    return data.invocation;
  }

  async listDistinctToolsForTenant(tenantId: string): Promise<string[]> {
    const { data } = await api.get<{ success: boolean; tools: string[] }>(
      `/super-admin/tenants/${tenantId}/tool-activity/tools`,
    );
    return data.tools || [];
  }
}

const toolInvocationService = new ToolInvocationService();
export default toolInvocationService;
